import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';

const NEWS_COLLECTION = 'newsPosts';
const PUBLIC_NEWS_BASE_URL = 'https://ubunifucollege.com/view-post.html?slug=';

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  const millis = toMillis(value);
  if (!millis) return 'Not published';
  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(millis));
};

const stripHtml = (value) => (
  String(value || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const getCardImage = (post) => post.coverImage || post.ogImage || '';

function NewsPostsComp() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, NEWS_COLLECTION), (snapshot) => {
      const nextPosts = snapshot.docs
        .map((item) => ({ docID: item.id, ...item.data() }))
        .sort((a, b) => (
          (toMillis(b.updatedAt) || b.updatedAtMs || toMillis(b.publishedAt) || b.publishedAtMs || 0) -
          (toMillis(a.updatedAt) || a.updatedAtMs || toMillis(a.publishedAt) || a.publishedAtMs || 0)
        ));

      setPosts(nextPosts);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load news posts:', error);
      setLoading(false);
      swal('Could not load posts', error.message || 'Please check your permissions and try again.', 'error');
    });

    return () => unsubscribe();
  }, []);

  const summary = useMemo(() => ({
    total: posts.length,
    published: posts.filter((post) => post.status === 'published' || post.published).length,
    drafts: posts.filter((post) => !(post.status === 'published' || post.published)).length,
  }), [posts]);

  const handleDelete = (post) => {
    swal({
      title: 'Delete this post?',
      text: `This will remove "${post.title || post.docID}" from the website.`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        await deleteDoc(doc(db, NEWS_COLLECTION, post.docID));
        swal('Deleted', 'The news post has been removed.', 'success');
      } catch (error) {
        console.error('Failed to delete news post:', error);
        swal('Could not delete post', error.message || 'Please try again.', 'error');
      }
    });
  };

  return (
    <div className='dashComp newsAdminPage'>
      <section className='newsAdminTop newsAdminTopList'>
        <div>
          <span className='newsAdminEyebrow'>Website CMS</span>
          <h2>News Posts</h2>
          <p>Manage the posts shown on the public News & Updates page.</p>
        </div>
        <div className='newsAdminTopRight'>
          <div className='newsAdminStats'>
            <div><span>{summary.total}</span><small>Total</small></div>
            <div><span>{summary.published}</span><small>Published</small></div>
            <div><span>{summary.drafts}</span><small>Drafts</small></div>
          </div>
          <Button variant='dark' className='newsAdminAddButton' onClick={() => navigate('/news-posts/new')}>
            Add Post
          </Button>
        </div>
      </section>

      <section className='newsAdminList newsAdminListFull'>
        <div className='newsAdminPanelHeader'>
          <div>
            <h4>All posts</h4>
            <p>Draft posts stay hidden from the public website until you publish them.</p>
          </div>
        </div>

        {loading ? (
          <div className='newsAdminLoading'>
            <Spinner animation='border' size='sm' />
            <span>Loading posts...</span>
          </div>
        ) : posts.length === 0 ? (
          <div className='newsAdminEmpty'>
            No posts yet. Use the Add Post button to create your first update.
          </div>
        ) : (
          <div className='newsArticleGrid'>
            {posts.map((post) => {
              const isPublished = post.status === 'published' || post.published;
              const publicUrl = `${PUBLIC_NEWS_BASE_URL}${encodeURIComponent(post.slug || post.docID)}`;
              const imageUrl = getCardImage(post);
              const excerpt = post.excerpt || stripHtml(post.content).slice(0, 160) || 'No excerpt has been added yet.';

              return (
                <article key={post.docID} className='newsArticleCard'>
                  <div className='newsArticleMedia'>
                    {imageUrl ? (
                      <img src={imageUrl} alt={post.title || 'News post image'} />
                    ) : (
                      <div className='newsArticleMediaFallback'>
                        <span>Ubunifu News</span>
                      </div>
                    )}
                    <span className={`newsArticleBadge ${isPublished ? 'newsArticleBadgePublished' : 'newsArticleBadgeDraft'}`}>
                      {isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className='newsArticleBody'>
                    <div className='newsArticleMetaRow'>
                      <span>{post.category || 'Updates'}</span>
                      <span>{formatDate(post.publishedAtMs || post.publishedAt)}</span>
                    </div>
                    <h3>{post.title || 'Untitled post'}</h3>
                    <p>{excerpt}</p>
                    <div className='newsArticleFooter'>
                      <small>{post.slug || post.docID}</small>
                      <div className='newsArticleActions'>
                        {isPublished && (
                          <a href={publicUrl} target='_blank' rel='noreferrer'>View</a>
                        )}
                        <button type='button' onClick={() => navigate(`/news-posts/edit/${encodeURIComponent(post.docID)}`)}>Edit</button>
                        <button type='button' className='danger' onClick={() => handleDelete(post)}>Delete</button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default NewsPostsComp;
